/**
 * Hook for polling data while page is visible, using the Page Visibility API
 * to pause polling when tab is backgrounded.
 */

import { useCallback, useEffect, useRef } from 'react';

interface UsePageVisibilityPollingOptions {
  /** Callback function to execute on each poll */
  onPoll: () => void;
  /** Polling interval in milliseconds */
  intervalMs: number;
  /** Whether polling should be active */
  enabled?: boolean;
}

/**
 * Polls data at regular intervals while the page is visible.
 * Automatically pauses when the tab is backgrounded and resumes when visible.
 *
 * @param options - Configuration for polling behavior
 */
export function usePageVisibilityPolling({
  onPoll,
  intervalMs,
  enabled = true,
}: UsePageVisibilityPollingOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);

  const startPolling = useCallback(() => {
    if (intervalRef.current || !enabled) return;

    isPollingRef.current = true;
    intervalRef.current = setInterval(onPoll, intervalMs);
  }, [onPoll, intervalMs, enabled]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (!enabled) return;

    if (document.hidden) {
      stopPolling();
    } else {
      // Page became visible - start polling if it was previously running
      startPolling();
    }
  }, [enabled, startPolling, stopPolling]);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    // Start polling immediately if page is visible
    if (!document.hidden) {
      startPolling();
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, startPolling, stopPolling, handleVisibilityChange]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    /** Whether polling is currently active */
    isPolling: isPollingRef.current,
    /** Manually start polling (respects page visibility) */
    startPolling,
    /** Manually stop polling */
    stopPolling,
  };
}